/**
 * Cliente HTTP del reverse proxy CDK (`/ntam|{nthw|ntva}/{hubSpotId}/{proxy+}`).
 * Authorizer Cognito: Bearer del ID token. No IAM, no x-api-key del micro.
 *
 * Solo se usa si hay `REACT_APP_NUA_PROXY_URL`. Si no, el resto del front
 * sigue en Amplify `/proxy/...` como hasta ahora.
 */
import { isCdkProxyConfigured, nuaProxyUrl } from './appConfig';
import { getIdToken } from './cognitoAuth';

export { isCdkProxyConfigured };

export function cdkProxyPath(service, hubSpotId, path) {
  const clean = String(path || '').replace(/^\/+/, '');
  const base = `/${service}/${encodeURIComponent(String(hubSpotId))}`;
  return clean ? `${base}/${clean}` : base;
}

export function splitProxyUrl(url) {
  const raw = String(url || '');
  const q = raw.indexOf('?');
  if (q === -1) return { path: raw, queryParams: undefined };
  const path = raw.slice(0, q);
  return {
    path,
    queryParams: Object.fromEntries(new URLSearchParams(raw.slice(q + 1))),
  };
}

const queryString = (params) => {
  if (!params || typeof params !== 'object') return '';
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value != null && String(value) !== '')
  ).toString();
  return qs ? `?${qs}` : '';
};

/**
 * Petición a una ruta nativa del CDK (`/me/accounts`, `/products/v1`,
 * `/platform/profiles`, `/users/{id}`, …). Lanza si no hay env, sesión,
 * o si el upstream no responde 2xx.
 */
export async function fetchCdkApi({ path, method = 'GET', body, queryParams }) {
  if (!isCdkProxyConfigured()) {
    throw new Error('Proxy CDK no configurado');
  }
  const clean = `/${String(path || '').replace(/^\/+/, '')}`;
  const url = `${nuaProxyUrl()}${clean}${queryString(queryParams)}`;
  const idToken = await getIdToken();
  if (!idToken) {
    const error = new Error('No hay sesión activa');
    error.status = 401;
    throw error;
  }
  const upper = String(method || 'GET').toUpperCase();
  const init = {
    method: upper,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  };
  if (body !== undefined && upper !== 'GET' && upper !== 'HEAD') {
    init.body = JSON.stringify(body);
  }

  const response = await fetch(url, init);
  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message = data?.message || data?.error || `HTTP ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    // Cuerpo completo del error (p. ej. la lista `errors` de una validación).
    error.data = data;
    throw error;
  }

  if (data == null) return {};
  if (typeof data === 'object') return data;
  return { value: data };
}

/**
 * Reenvía al reverse proxy `/{servicio}/{hubSpotId}/...`.
 */
export async function fetchCdkProxy({
  service,
  hubSpotId,
  path,
  method = 'GET',
  body,
  queryParams,
}) {
  if (!service) throw new Error('Falta el servicio del proxy');
  if (!hubSpotId) throw new Error('Falta hubSpotId para el proxy CDK');
  return fetchCdkApi({
    path: cdkProxyPath(service, hubSpotId, path),
    method,
    body,
    queryParams,
  });
}
